import React, { useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, Mail, Linkedin } from 'lucide-react';
import clsx from 'clsx';

type FooterLink = {
  label: string;
  href: string;
};

type FooterSectionProps = {
  title: string;
  links: FooterLink[];
  isOpen: boolean;
  onToggle: () => void;
};

const FooterSection = ({ title, links, isOpen, onToggle }: FooterSectionProps) => {
  return (
    <div className="border-b border-fp-navy-700 md:border-none last:border-none">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full py-4 md:py-0 md:mb-4 group text-left"
      >
        <h3 className="font-mono font-semibold text-[12px] tracking-[0.16em] uppercase text-fp-slate-300 group-hover:text-fp-emerald-400 md:group-hover:text-fp-slate-100 transition-colors">
          {title}
        </h3>
        <ChevronDown
          className={clsx(
            "w-4 h-4 text-slate-500 transition-transform md:hidden",
            isOpen && "rotate-180"
          )}
        />
      </button>

      <div className={clsx(
        "overflow-hidden transition-all duration-300 md:h-auto md:opacity-100",
        isOpen ? "max-h-64 opacity-100 mb-4" : "max-h-0 opacity-0 md:max-h-none md:mb-0"
      )}>
        <ul className="space-y-3">
          {links.map((link) => (
            <li key={link.label}>
              <Link
                to={link.href}
                className="font-inter text-[15px] text-fp-slate-300 hover:text-fp-emerald-400 transition-colors block"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export function FairPathFooter() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <footer className="bg-fp-navy-900 w-full border-t border-fp-amber-500">
      <div className="max-w-[1280px] mx-auto px-6 py-12 md:py-16">
        
        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 mb-12">
          
          {/* Column 1: Brand */}
          <div className="col-span-1 space-y-4">
            <Link to="/" className="font-inter font-bold text-2xl text-white tracking-[-0.03em] hover:text-fp-slate-100 transition-colors inline-block">
              FairPath.
            </Link>
            <p className="font-inter text-[15px] text-fp-slate-300 leading-relaxed">
              Property-tax evidence organized for homeowners.
            </p>
            <div className="flex items-center gap-4 pt-2">
              <a
                href="https://www.linkedin.com/company/fairpathapp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fp-slate-400 hover:text-fp-emerald-500 transition-colors duration-150"
                aria-label="FairPath on LinkedIn"
              >
                <Linkedin className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61571980163613"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fp-slate-400 hover:text-fp-emerald-500 transition-colors duration-150"
                aria-label="FairPath on Facebook"
              >
                {/* Facebook — inline SVG to avoid Vite dep cache issues */}
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a
                href="https://nextdoor.com/page/fairpath-austin-tx/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fp-slate-400 hover:text-fp-emerald-500 transition-colors duration-150"
                aria-label="FairPath on Nextdoor"
              >
                {/* Nextdoor — no Lucide icon; inline house SVG */}
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
                  <path d="M9 21V14h6v7" />
                </svg>
              </a>
              <a
                href="https://x.com/fair_pathapp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fp-slate-400 hover:text-fp-emerald-500 transition-colors duration-150"
                aria-label="FairPath on X (Twitter)"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Columns 2-4: Links */}
          <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-8">
            <FooterSection
              title="Product"
              links={[
                { label: 'Check My Property', href: '/search' },
                { label: 'Pricing', href: '/pricing' },
                { label: 'How It Works', href: '/how-it-works' },
                { label: 'Property Tax Appeal Software', href: '/property-tax-appeal-software' },
                { label: 'Shield', href: '/shield' },
                { label: 'Coverage', href: '/coverage' },
                { label: 'FAQ', href: '/faq' },
              ]}
              isOpen={openSection === 'product'}
              onToggle={() => toggleSection('product')}
            />
            <FooterSection
              title="Company"
              links={[
                { label: 'About', href: '/about' },
                { label: 'Methodology & Corrections', href: '/methodology' },
                { label: 'Blog', href: '/blog' },
                { label: 'Contact', href: '/contact' },
              ]}
              isOpen={openSection === 'company'}
              onToggle={() => toggleSection('company')}
            />
            <FooterSection
              title="Compare"
              links={[
                { label: 'vs. Ownwell', href: '/vs-ownwell' },
                { label: 'vs. O\'Connor', href: '/vs-oconnor' },
                { label: 'vs. AppealDesk', href: '/vs-appealdesk' },
              ]}
              isOpen={openSection === 'compare'}
              onToggle={() => toggleSection('compare')}
            />
            <FooterSection
              title="Legal"
              links={[
                { label: 'Terms of Service', href: '/terms' },
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Disclaimer', href: '/disclaimer' },
              ]}
              isOpen={openSection === 'legal'}
              onToggle={() => toggleSection('legal')}
            />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-fp-slate-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          
          {/* Copyright */}
          <div className="text-fp-slate-500 text-xs font-inter order-2 md:order-1">
            © 2026 FairPath. All rights reserved.
          </div>

          {/* Support Email */}
          <div className="flex items-center gap-2 text-fp-slate-400 text-xs font-inter order-1 md:order-2 hover:text-white transition-colors cursor-pointer">
            <Mail className="w-3 h-3" />
            support@fairpath.app
          </div>

        </div>

        {/* Founder attribution */}
        <p className="text-center text-xs text-fp-slate-500 mt-6 font-inter">
          Built by a real estate and finance professional.
        </p>
      </div>
    </footer>
  );
}
